import { useMemo, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    MenuItem,
    TextField,
    Typography,
    CircularProgress,
    Tooltip,
    Menu,
    Paper,
    Alert,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
    FilterList,
    Assessment,
    Analytics,
    TableChart,
    Refresh,
    Mail,
} from "@mui/icons-material";
import { useMutation, useQuery } from "@tanstack/react-query";
import logger from "../../helpers/logger";
import { useAuthLite } from "../../hooks/useAuthLite";
import axiosIns from "../../common/interceptor/axios-interceptor";
import { toast } from "react-toastify";
import TOASTER from "../../config/toaster";
import { DataGrid } from "@mui/x-data-grid";
import { COMMON_MSG } from "../../common/messages";
import { getFileNamePrefixWithDate } from "../../helpers/file-export";
import { exportToCsv, exportToExcel } from "../../api/export-api-service";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import useResponsiveConfig from "../../config/responsive-style";
import { addDays, startOfDay, parse } from "date-fns";
import { utcToZonedTime, formatInTimeZone, zonedTimeToUtc } from "date-fns-tz";
import CustomDatePicker from "../../common/components/DatePicker";

const SESSION_KEY = "selectedFastRacksReportDate";

// Central-time-aware date disable function for the date picker
// Disables dates beyond tomorrow in Central Time (America/Chicago)
const disableFutureExceptTomorrowCentral = (date) => {
    // Get current time in Central Time
    const centralNow = utcToZonedTime(new Date(), "America/Chicago");
    // Max allowed date: tomorrow in Central Time (start of day for clean comparison)
    const centralTomorrow = startOfDay(addDays(centralNow, 1));
    // Convert the picker date to Central Time for consistent comparison
    const pickerDateCentral = utcToZonedTime(date, "America/Chicago");
    const pickerDateCentralStartOfDay = startOfDay(pickerDateCentral);
    // Disable if picker date is after tomorrow in Central Time
    return pickerDateCentralStartOfDay > centralTomorrow;
};
const isDev = process.env.NODE_ENV !== "production";

const FastRacksReport = () => {
    const { validateAuth } = useAuthLite();
    const getInitialDate = () => {
        const savedDateStr = sessionStorage.getItem(SESSION_KEY);
        // Calculate current time in Central Time (America/Chicago) using date-fns-tz
        const centralNow = utcToZonedTime(new Date(), "America/Chicago");
        // Max allowed date: tomorrow in Central Time (start of day for clean comparison)
        const maxDate = startOfDay(addDays(centralNow, 1));

        if (!savedDateStr) {
            // Default to tomorrow in Central Time for consistency
            return maxDate;
        }

        // Parse the saved "yyyy-MM-dd" CT date string
        // This creates a Date interpreted as midnight on that day
        const parsedLocalDate = parse(savedDateStr, "yyyy-MM-dd", new Date());
        
        // Convert the parsed date (interpreted as CT midnight) to a proper UTC instant
        // that represents midnight in Central Time on that date
        const ctMidnight = zonedTimeToUtc(parsedLocalDate, "America/Chicago");
        
        // Convert back to CT-zoned representation for comparison with maxDate
        const zonedParsedDate = utcToZonedTime(ctMidnight, "America/Chicago");
        const zonedParsedDateStartOfDay = startOfDay(zonedParsedDate);

        // Clamp to maxDate if saved date exceeds tomorrow (Central Time)
        return zonedParsedDateStartOfDay > maxDate ? maxDate : zonedParsedDate;
    };
    const [reportData, setReportData] = useState(null);
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [emailAnchorEl, setEmailAnchorEl] = useState(null);
    const [showToaster, setShowToaster] = useState(true);
    const [selectedDate, setSelectedDate] = useState(getInitialDate());
    const { DATAGRID_STYLE } = useResponsiveConfig();

    const generateReportMutation = useMutation({
        mutationFn: async (requestData) => {
            logger.log("🚀 Generating report");

            if (isDev) {
                logger.log("🌐 Making API request to:", "/api/fastracksreport/generate");
                logger.log("🔑 Authorization token present:", !!axiosIns.defaults.headers?.Authorization);
            }

            const response = await axiosIns.post(
                "/fastracksreport/generate",
                requestData
            );

            logger.log("📡 Response status:", response.status);
            if (isDev) {
                logger.log("📡 Response status text:", response.statusText);
                logger.log("📡 Response headers:", response.headers);
            }
            return response.data;
        },
        onSuccess: (data) => {
            logger.log("✅ Report generated successfully");

            if (isDev) {
                logger.log("📊 Report metadata:", data.metadata);
                logger.log("📈 Report summary:", data.summary);

                // Debug the SQL query and parameters
                if (data.metadata?.sqlQuery) {
                    logger.log("🔍 SQL Query executed:", data.metadata.sqlQuery);
                    logger.log(
                        "🔍 SQL Parameters:",
                        JSON.stringify(data.metadata.parameters, null, 2)
                    );
                    logger.log(
                        "🔍 Applied Filters:",
                        JSON.stringify(data.metadata.appliedFilters, null, 2)
                    );
                } else {
                    logger.log(
                        "⚠️ No SQL query in metadata - check includeMetadata setting"
                    );
                }

                // Debug the first few data records to see their structure
                if (data.data && data.data.length > 0) {
                    logger.log("🔍 First data record sample:", data.data[0]);
                    logger.log("🔍 Data record keys:", Object.keys(data.data[0]));
                }
            }

            setReportData(data);
            if (showToaster) {
                toast.success("Report generated successfully!", TOASTER.SUCCESS_CONFIG);
            }
            logger.log("Report generated, execution time:", data.metadata?.executionTime);
        },
        onError: (error) => {
            toast.error(
                `Error generating report: ${error.message}`,
                TOASTER.ERROR_CONFIG
            );
            logger.error("Report generation error:", error);
        },
    });

    // Send Email
    const sendEmailMutation = useMutation({
        mutationFn: async (requestData) => {
            const response = await axiosIns.post(
                "/fastracksreport/export-file",
                requestData
            );

            logger.log("📡 Export request completed with status:", response.status);
            return response.data;
        },
        onSuccess: (data) => {
            toast.success("Mail sent successfully!", TOASTER.SUCCESS_CONFIG);
            logger.log("✅ Export response:", data);
        },
        onError: (error) => {
            toast.error(
                `Failed to send mail: ${error.message}`,
                TOASTER.ERROR_CONFIG
            );
            logger.error("Export error details:", error);
        },
    });

    const handleGenerateReport = async () => {
        setShowToaster(true);

        if (!(await validateAuth())) return;

        const requestData = {
            inputDate: selectedDate,
        };

        logger.log("📤 Final request data:", requestData);
        generateReportMutation.mutate(requestData);
    }

    const handleUpdateDate = async (selectedDate) => {
        if (!selectedDate) return;

        logger.log("Update Date event triggered:", selectedDate);

        setSelectedDate(selectedDate);
        // Store as CT date-only string (yyyy-MM-dd) to prevent timezone drift
        // when rehydrating the date from sessionStorage
        const ctDateString = formatInTimeZone(selectedDate, "America/Chicago", "yyyy-MM-dd");
        sessionStorage.setItem(SESSION_KEY, ctDateString);
    };

    // Supplier Grouping Data
    const hasAnyValue = (rows, field) =>
        rows.some(row => row[field] !== null && row[field] !== undefined);

    const groupSupplierPricing = (data) => {
        const grouped = {};

        data.forEach((item, index) => {
            const supplier = item.supplier ?? "unknown";
            const product = item.product ?? "unknown";
            const supplierKey = item.supplier ?? "unknown-supplier";

            if (!grouped[supplierKey]) {
                grouped[supplierKey] = [];
            }

            // Include index for uniqueness when supplier/product may be null or duplicated
            grouped[supplierKey].push({
                id: `supplier-${index}-${supplier}-${product}`,
                supplier: item.supplier,
                product: item.product,
                nashville: item.nashville,
                chattanooga: item.chattanooga,
                knoxville: item.knoxville,
                bainbridge: item.bainbridge,
            });
        });

        return grouped;
    };

    const getSupplierColumns = (supplierName, rows) => {
        const columns = [
            {
                field: "product",
                headerName: supplierName,
                minWidth: 150,
                flex: 1
            },
        ];

        if (hasAnyValue(rows, "nashville")) {
            columns.push({ field: "nashville", headerName: "Nashville", minWidth: 100, flex: 0.5 });
        }

        if (hasAnyValue(rows, "chattanooga")) {
            columns.push({ field: "chattanooga", headerName: "Chattanooga", minWidth: 100, flex: 0.7 });
        }

        if (hasAnyValue(rows, "knoxville")) {
            columns.push({ field: "knoxville", headerName: "Knoxville", minWidth: 100, flex: 0.5 });
        }

        if (hasAnyValue(rows, "bainbridge")) {
            columns.push({ field: "bainbridge", headerName: "Bainbridge", minWidth: 100, flex: 0.5 });
        }

        return columns;
    };

    // TSE Data
    const validCities = ["nashville", "chattanooga", "knoxville", "bainbridge"];

    const buildTseRows = (data) => {
        const rows = {};
        let nullProductCounter = 0;

        data.forEach((item, index) => {
            const product = item.rackDescription;
            // Use a unique key for null/undefined products to prevent merging
            const productKey = product ?? `unknown-product-${nullProductCounter++}`;

            if (!rows[productKey]) {
                rows[productKey] = {
                    // Include index prefix for guaranteed uniqueness
                    id: product != null ? `tse-${product}` : `tse-unknown-${index}`,
                    product: product ?? null,
                    nashville: null,
                    chattanooga: null,
                    knoxville: null,
                    bainbridge: null,
                };
            }

            const city = (item.terminalCity ?? "").toLowerCase();
            if (city && validCities.includes(city)) {
                rows[productKey][city] = item.postedPriceWithAdder ?? item.postedPrice;
            }
        });

        return Object.values(rows);
    };

    const tseColumns = [
        { field: "product", headerName: "TSE RACK", minWidth: 150, flex: 1 },
        { field: "nashville", headerName: "Nashville", minWidth: 100, flex: 0.5 },
        { field: "chattanooga", headerName: "Chattanooga", minWidth: 100, flex: 0.7 },
        { field: "knoxville", headerName: "Knoxville", minWidth: 100, flex: 0.5 },
        { field: "bainbridge", headerName: "Bainbridge", minWidth: 100, flex: 0.5 },
    ];

    const gasTaxColumns = [
        {
            field: "settingsNote",
            headerName: "Description",
            flex: 1.5,
            sortable: false,
        },
        {
            field: "settingsValue",
            headerName: "Value",
            flex: 0.5,
            sortable: false,
            align: "right",
            headerAlign: "right",
        },
    ];

    const buildSupplierRows = (supplierPricing) =>
        supplierPricing.map(item => ({
            Supplier: item.supplier,
            Product: item.product,
            Nashville: item.nashville ?? "",
            Chattanooga: item.chattanooga ?? "",
            Knoxville: item.knoxville ?? "",
            Bainbridge: item.bainbridge ?? "",
        }));

    const buildTseRowsForExport = (tsePricing) => {
        const grouped = {};

        tsePricing
            .filter(r => r.fastRacksRank === 2)
            .forEach((item, index) => {
                // Use a stable unique key when rackDescription is null/undefined to prevent merging
                const productKey = item.rackDescription ?? `__null_${index}`;
                const productDisplay = item.rackDescription ?? "";

                if (!grouped[productKey]) {
                    grouped[productKey] = {
                        Supplier: "TSE RACK",
                        Product: productDisplay,
                        Nashville: "",
                        Chattanooga: "",
                        Knoxville: "",
                        Bainbridge: "",
                    };
                }

                const value = item.postedPriceWithAdder ?? item.postedPrice;
                const city = (item.terminalCity ?? "").toLowerCase();

                if (city && validCities.includes(city)) {
                    // Capitalize first letter to match export column keys
                    const cityKey = city.charAt(0).toUpperCase() + city.slice(1);
                    grouped[productKey][cityKey] = value;
                }
            });

        return Object.values(grouped);
    };

    // Tax data for export
    const buildTaxRowsForExport = (gasTaxData) => {
        if (!gasTaxData?.length) return [];

        return gasTaxData.map(item => ({
            Supplier: item.settingsNote,
            Product: item.settingsValue,
            Nashville: "",
            Chattanooga: "",
            Knoxville: "",
            Bainbridge: "",
        }));
    };

    const buildFinalExportData = (supplierPricing, tsePricing, taxFreight) => {
        const rows = [];

        // Supplier data
        rows.push(...buildSupplierRows(supplierPricing));

        rows.push({}, {});

        // TSE header
        rows.push({
            Supplier: "TSE RACK",
            Product: "Product",
            Nashville: "Nashville",
            Chattanooga: "Chattanooga",
            Knoxville: "Knoxville",
            Bainbridge: "Bainbridge",
        });

        rows.push(...buildTseRowsForExport(tsePricing));

        rows.push({}, {});

        // Tax header
        rows.push({
            Supplier: "Taxes & Freight",
            Product: "Value",
        });

        rows.push(...buildTaxRowsForExport(taxFreight));

        return rows;
    };

    const handleSendEmailClick = (event) => {
        setEmailAnchorEl(event.currentTarget);
    };

    const handleSendEmailClose = () => {
        setEmailAnchorEl(null);
    };

    const handleSendEmail = async (isCSV = false) => {
        try {
            const supplierPricing = reportData?.supplierPricing ?? [];
            const tsePricing = reportData?.tsePricing ?? [];
            const taxFreight = reportData?.taxFreight ?? [];

            if (!supplierPricing.length && !tsePricing.length) {
                handleSendEmailClose();
                toast.warn(COMMON_MSG.NoDataFound, { autoClose: TOASTER.AUTO_CLOSE });
                return;
            }

            handleSendEmailClose();
            const exportData = buildFinalExportData(
                supplierPricing,
                tsePricing,
                taxFreight
            );

            const fileName = getFileNamePrefixWithDate("Fast_Racks_Report");

            const requestData = {
                fileName,
                data: exportData,
                exportType: isCSV ? "csv" : "excel",
            };
            sendEmailMutation.mutate(requestData);
        } catch (error) {
            const formatLabel = isCSV ? "CSV" : "Excel";
            logger.error(`Error sending email with ${formatLabel}:`, error);
            toast.error(`Failed to send email with ${formatLabel} file.`);
        }
    }

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExportExcel = async () => {
        let toastId = null;
        try {
            const supplierPricing = reportData?.supplierPricing ?? [];
            const tsePricing = reportData?.tsePricing ?? [];
            const taxFreight = reportData?.taxFreight ?? [];

            if (!supplierPricing.length && !tsePricing.length) {
                handleExportClose();
                toast.warn(COMMON_MSG.NoDataFound, { autoClose: TOASTER.AUTO_CLOSE });
                return;
            }
            toastId = toast.loading("Generating Excel file...");

            const exportData = buildFinalExportData(
                supplierPricing,
                tsePricing,
                taxFreight
            );

            const fileName = getFileNamePrefixWithDate("Fast_Racks_Report");

            const res = await exportToExcel({
                data: exportData,
                fileName,
                sheetName: "Fast Racks Report",
            });

            if (res.success) {
                toast.update(toastId, {
                    render: "Excel file downloaded successfully",
                    type: "success",
                    isLoading: false,
                    autoClose: TOASTER.AUTO_CLOSE,
                });
                handleExportClose();
            } else {
                handleExportClose();
                throw new Error(res.message);
            }
        } catch (error) {
            logger.error(error);
            if (toastId) {
                toast.update(toastId, {
                    render: `Excel export failed: ${error.message}`,
                    type: "error",
                    isLoading: false,
                    autoClose: TOASTER.AUTO_CLOSE,
                });
            } else {
                toast.error(`Excel export failed: ${error.message}`, TOASTER.ERROR_CONFIG);
            }
        }
    };

    const handleExportCSV = async () => {
        let toastId = null;
        try {
            const supplierPricing = reportData?.supplierPricing ?? [];
            const tsePricing = reportData?.tsePricing ?? [];
            const taxFreight = reportData?.taxFreight ?? [];

            if (!supplierPricing.length && !tsePricing.length) {
                handleExportClose();
                toast.warn(COMMON_MSG.NoDataFound, { autoClose: TOASTER.AUTO_CLOSE });
                return;
            }

            toastId = toast.loading("Generating CSV file...");

            const exportData = buildFinalExportData(
                supplierPricing,
                tsePricing,
                taxFreight
            );

            const fileName = getFileNamePrefixWithDate("Fast_Racks_Report");

            const res = await exportToCsv({
                data: exportData,
                fileName,
                sheetName: "Fast Racks Report",
            });

            if (res.success) {
                toast.update(toastId, {
                    render: "CSV file downloaded successfully",
                    type: "success",
                    isLoading: false,
                    autoClose: TOASTER.AUTO_CLOSE,
                });
                handleExportClose();
            } else {
                handleExportClose();
                throw new Error(res.message);
            }
        } catch (error) {
            logger.error(error);
            if (toastId) {
                toast.update(toastId, {
                    render: `CSV export failed: ${error.message}`,
                    type: "error",
                    isLoading: false,
                    autoClose: TOASTER.AUTO_CLOSE,
                });
            } else {
                toast.error(`CSV export failed: ${error.message}`, TOASTER.ERROR_CONFIG);
            }
        }
    };

    return (
        <Box sx={{ pt: 2 }}>
            <Typography
                variant="h4"
                gutterBottom
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
            >
                <Assessment />
                Fast Racks Report
            </Typography>

            <Card elevation={3} sx={{ mb: 3 }}>
                <CardContent>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                            <FilterList />
                            Filters
                        </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "start", gap: 2 }}>
                        {/* Report Date */}
                        <CustomDatePicker
                            label="Report Date"
                            value={selectedDate}
                            onChange={(newValue) => {
                                if (newValue) {
                                    setSelectedDate(newValue);
                                    handleUpdateDate(newValue);
                                }
                            }}
                            shouldDisableDate={disableFutureExceptTomorrowCentral}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    sx={{ minWidth: 150 }}
                                    size="small"
                                />
                            )}
                            sx={{ minWidth: { xs: "120px", sm: "155px" } }}
                        />

                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => handleGenerateReport()}
                            disabled={generateReportMutation.isPending}
                            startIcon={
                                generateReportMutation.isPending ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    <Analytics />
                                )
                            }
                            sx={{ minWidth: 200 }}
                        >
                            {generateReportMutation.isPending
                                ? "Generating..."
                                : "Generate Report"}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {/* Results Section */}
            {reportData && (
                <Card elevation={3}
                    sx={{
                        ...DATAGRID_STYLE,
                        maxWidth: { xs: "calc(100vw - 20px)", sm: DATAGRID_STYLE.maxWidth }
                    }}
                >
                    <CardContent>
                        <Box
                            sx={{
                                display: { md: "flex", sm: "block" },
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{ display: "flex", alignItems: "center", gap: 1 }}
                            >
                                <TableChart />
                                Report Results
                            </Typography>
                            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                                <Tooltip title="Refresh Report">
                                    <Button
                                        onClick={() => {
                                            handleGenerateReport()
                                        }}
                                        variant="outlined"
                                        startIcon={<Refresh />}
                                    >
                                        Refresh
                                    </Button>
                                </Tooltip>

                                <Tooltip title="Send Email">
                                    <Button
                                        onClick={handleSendEmailClick}
                                        disabled={sendEmailMutation.isPending}
                                        variant="contained"
                                        startIcon={<Mail />}
                                    >
                                        {sendEmailMutation.isPending
                                            ? "Sending..."
                                            : "Send Email"}
                                    </Button>
                                </Tooltip>
                                {/* Download Options Menu */}
                                <Menu
                                    anchorEl={emailAnchorEl}
                                    open={Boolean(emailAnchorEl)}
                                    onClose={handleSendEmailClose}
                                    disableScrollLock={true}
                                    keepMounted={true}
                                >
                                    <MenuItem onClick={() => handleSendEmail(true)}>CSV</MenuItem>
                                    <MenuItem onClick={() => handleSendEmail()}>Excel</MenuItem>
                                </Menu>

                                <Tooltip title="Export Data">
                                    <Button
                                        onClick={handleExportClick}
                                        disabled={sendEmailMutation.isPending}
                                        variant="outlined"
                                        startIcon={<FileDownloadOutlinedIcon />}
                                    >
                                        Export Data
                                    </Button>
                                </Tooltip>
                                {/* Download Options Menu */}
                                <Menu
                                    anchorEl={exportAnchorEl}
                                    open={Boolean(exportAnchorEl)}
                                    onClose={handleExportClose}
                                    disableScrollLock={true}
                                    keepMounted={true}
                                >
                                    <MenuItem onClick={handleExportCSV}>Export CSV</MenuItem>
                                    <MenuItem onClick={handleExportExcel}>Export Excel</MenuItem>
                                </Menu>
                            </Box>
                        </Box>

                        <Alert severity="info" sx={{ mb: 2, alignItems: "flex-start" }}>
                            <div>
                                <div>
                                    <strong>⏱ Generated in:</strong> {reportData?.metadata?.executionTime ?? "N/A"}
                                </div>
                                <div style={{ margin: 0 }}>
                                    Report Date: {reportData?.metadata?.parameters?.inputDate?.split("T")[0] ?? "—"}
                                </div>
                            </div>
                        </Alert>

                        <Grid container spacing={3} alignItems="stretch">
                            {/* LEFT */}
                            {(reportData?.supplierPricing ?? []).length > 0 && (
                                <Grid
                                    item
                                    size={{ sm: 12, md: 6 }}
                                >
                                    {Object.entries(groupSupplierPricing(reportData?.supplierPricing ?? []))
                                        .map(([supplierName, rows]) => (
                                            <Paper
                                                key={supplierName}
                                                elevation={2}
                                                sx={{ mb: 2, p: 1.5, width: "100%" }}
                                            >
                                                <DataGrid
                                                    autoHeight
                                                    rows={rows}
                                                    columns={getSupplierColumns(supplierName, rows)}
                                                    hideFooter
                                                    disableColumnMenu
                                                    disableRowSelectionOnClick
                                                />
                                            </Paper>
                                        ))}
                                </Grid>
                            )}

                            {/* RIGHT */}
                            {(reportData?.tsePricing ?? []).length > 0 && (
                                <Grid
                                    item
                                    size={{
                                        sm: 12,
                                        md: 6,
                                    }}
                                >
                                    <Paper elevation={2} sx={{ mb: 2, p: 1.5, width: "100%" }}>
                                        <DataGrid
                                            autoHeight
                                            rows={buildTseRows(reportData?.tsePricing ?? [])}
                                            columns={tseColumns}
                                            hideFooter
                                            disableColumnMenu
                                            disableRowSelectionOnClick
                                        />
                                    </Paper>

                                    <Paper elevation={2} sx={{ p: 1.5, mt: 2 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 700, mb: 1 }}>
                                            Taxes & Freight
                                        </Typography>

                                        <DataGrid
                                            getRowId={(row) => row.settingsKey}
                                            rows={reportData?.taxFreight || []}
                                            columns={gasTaxColumns}
                                            hideFooter
                                            disableColumnMenu
                                            disableRowSelectionOnClick
                                            sx={{
                                                border: "none",
                                                "& .MuiDataGrid-columnHeaders": {
                                                    fontWeight: 600,
                                                },
                                                "& .MuiDataGrid-cell": {
                                                    fontSize: 14,
                                                    fontWeight: 400,
                                                },
                                            }}
                                        />
                                    </Paper>
                                </Grid>
                            )}
                        </Grid>
                    </CardContent>
                </Card>
            )}
        </Box>
    )
};

export default FastRacksReport;